import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Quote, ArrowLeft, ArrowRight } from "lucide-react";
import CTAButton from "../CTAButton";
import { PROOF } from "../../data/content";

function Card({ p }) {
  return (
    <article
      data-testid="prueba-card"
      className="snap-center shrink-0 w-[80vw] sm:w-[360px] md:w-[400px]"
    >
      <div className="relative aspect-square overflow-hidden border border-white/[0.08] group">
        <img
          src={p.img}
          alt={p.recovered}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
      </div>
      <div className="pt-7">
        <span className="block font-display text-[10px] tracking-[0.32em] uppercase text-[#D4AF37] mb-4">
          {p.recovered}
        </span>
        <Quote size={22} className="text-[#D4AF37]/40 mb-3" />
        <blockquote className="font-serif italic text-white leading-[1.25] tracking-tight text-xl md:text-2xl text-balance">
          “{p.quote}”
        </blockquote>
        <figcaption className="mt-5 font-display text-[11px] tracking-[0.2em] uppercase text-white/60">
          {p.name}
        </figcaption>
      </div>
    </article>
  );
}

export default function Prueba() {
  const trackRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 8);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 8);
  };

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const amount = Math.min(el.clientWidth * 0.85, 440);
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section data-testid="act-prueba" className="relative bg-[#030303] py-20 md:py-28 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12 md:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1 }}
            className="max-w-[24ch]"
          >
            <span className="block font-display text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-7">
              La prueba
            </span>
            <h2 className="font-serif text-white leading-[1.05] tracking-tight text-4xl sm:text-5xl md:text-6xl">
              El cuerpo cambió. <span className="italic gold-text">La persona, primero.</span>
            </h2>
          </motion.div>

          {/* Flechas (escritorio) */}
          <div className="hidden md:flex items-center gap-3">
            <button
              data-testid="prueba-prev"
              onClick={() => scrollBy(-1)}
              disabled={atStart}
              aria-label="Anterior"
              className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white/70 transition-all duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-25 disabled:hover:border-white/20 disabled:hover:text-white/70"
            >
              <ArrowLeft size={18} />
            </button>
            <button
              data-testid="prueba-next"
              onClick={() => scrollBy(1)}
              disabled={atEnd}
              aria-label="Siguiente"
              className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white/70 transition-all duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-25 disabled:hover:border-white/20 disabled:hover:text-white/70"
            >
              <ArrowRight size={18} />
            </button>
          </div>
        </div>

        {/* Carrusel */}
        <div
          ref={trackRef}
          onScroll={onScroll}
          className="flex gap-5 md:gap-8 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-6 px-6 md:mx-0 md:px-0 no-scrollbar"
        >
          {PROOF.map((p, i) => (
            <Card key={i} p={p} />
          ))}
        </div>

        <div className="mt-14 md:mt-16 flex justify-center">
          <CTAButton testid="prueba-cta" />
        </div>
      </div>
    </section>
  );
}
